/**
 * AIFeedback — recruiter/admin page listing raw AI-feedback events with a
 * rollup card up top. Deep-linked from the dashboard AIAccuracyCard so
 * teams can inspect exactly which AI outputs got flagged as false positives
 * (or dismissed) and iterate on prompts.
 *
 * Filters: days window (7/14/30/90), feature (red_flag / ai_summary / etc.),
 * action (accept, dismiss, false_positive, snooze, complete).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, Filter } from "lucide-react";

const FEATURE_OPTS = ["", "red_flag", "ai_summary", "skill_match", "urgency_task", "suggestion"];
const ACTION_OPTS = ["", "accept", "complete", "false_positive", "dismiss", "snooze", "edit"];

function fmtDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso.slice(0, 16); }
}

export default function AIFeedback() {
    const [params, setParams] = useSearchParams();
    const days = Number(params.get("days") || 7);
    const feature = params.get("feature") || "";
    const action = params.get("action") || "false_positive"; // dashboard deep-link default

    const [summary, setSummary] = useState(null);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                const [s, l] = await Promise.all([
                    api.get("/ai-feedback/summary", { params: { days, feature: feature || undefined } }),
                    api.get("/ai-feedback", { params: { days, feature: feature || undefined, action: action || undefined, limit: 200 } }),
                ]);
                setSummary(s.data);
                setItems(l.data?.items || []);
            } catch { setSummary(null); setItems([]); }
            finally { setLoading(false); }
        })();
    }, [days, feature, action]);

    const patch = (k, v) => {
        const next = new URLSearchParams(params);
        if (v == null || v === "") next.delete(k); else next.set(k, String(v));
        setParams(next);
    };

    const acc = summary?.accuracy_pct;
    const accColor = acc == null ? "text-muted-foreground"
        : acc >= 80 ? "text-emerald-700"
        : acc >= 60 ? "text-amber-600"
        : "text-rose-600";

    return (
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4" data-testid="ai-feedback-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" /> AI feedback loop
                </div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">AI accuracy audit</h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">
                    Every 👍/👎 your team casts on an AI red flag, summary, urgency or match feeds into this ledger.
                    Use it to spot prompts that consistently mislead recruiters and refine them.
                </p>
            </div>

            {/* Rollup */}
            <div className="grid md:grid-cols-4 gap-px bg-border">
                <Stat label={`Accuracy (last ${days}d)`} value={acc == null ? "—" : `${acc}%`} className={accColor} />
                <Stat label="Total events" value={summary?.total ?? 0} />
                <Stat label="Accepted" value={(summary?.actions?.accept || 0) + (summary?.actions?.complete || 0)} />
                <Stat label="Flagged wrong" value={(summary?.actions?.false_positive || 0) + (summary?.actions?.dismiss || 0)} />
            </div>

            {/* Filters */}
            <div className="border border-border bg-white p-3 sm:p-4 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    <Filter className="w-3.5 h-3.5" /> Filter
                </div>
                <FilterSelect label="Window" value={String(days)} onChange={(v) => patch("days", v)}
                    options={[["7", "7 days"], ["14", "14 days"], ["30", "30 days"], ["90", "90 days"]]} testId="filter-days" />
                <FilterSelect label="Feature" value={feature} onChange={(v) => patch("feature", v)}
                    options={FEATURE_OPTS.map((f) => [f, f || "All features"])} testId="filter-feature" />
                <FilterSelect label="Action" value={action} onChange={(v) => patch("action", v)}
                    options={ACTION_OPTS.map((f) => [f, f || "All actions"])} testId="filter-action" />
            </div>

            {/* Top mismatch reasons */}
            {summary?.top_reasons?.length > 0 && (
                <div className="border border-border bg-white p-3 sm:p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Top mismatch reasons ({days}d)</div>
                    <ul className="space-y-1">
                        {summary.top_reasons.map((r) => (
                            <li key={r.reason} className="text-sm flex items-baseline gap-3">
                                <span className="font-mono text-xs text-ikb w-8 shrink-0">{r.count}×</span>
                                <span className="truncate">{r.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Raw events table */}
            <div className="border border-border bg-white overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="ai-feedback-table">
                    <thead className="bg-secondary/40 text-[10px] font-mono uppercase tracking-widest">
                        <tr>
                            <th className="text-left px-3 py-2 border-b border-border">When</th>
                            <th className="text-left px-3 py-2 border-b border-border">Who</th>
                            <th className="text-left px-3 py-2 border-b border-border">Feature</th>
                            <th className="text-left px-3 py-2 border-b border-border">Action</th>
                            <th className="text-left px-3 py-2 border-b border-border">Reason</th>
                            <th className="text-left px-3 py-2 border-b border-border">Target</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={6} className="text-center py-8 text-sm text-muted-foreground">No feedback events match these filters.</td></tr>
                        ) : items.map((f) => (
                            <tr key={f.id} className="border-b border-border hover:bg-secondary/30" data-testid={`ai-feedback-row-${f.id}`}>
                                <td className="px-3 py-2 text-xs font-mono text-muted-foreground whitespace-nowrap">{fmtDate(f.created_at)}</td>
                                <td className="px-3 py-2 text-xs">{f.user_name || "—"}</td>
                                <td className="px-3 py-2 text-xs"><Chip>{f.feature}</Chip></td>
                                <td className="px-3 py-2 text-xs"><ActionChip action={f.action} /></td>
                                <td className="px-3 py-2 text-xs text-foreground/80 max-w-md truncate" title={f.reason}>{f.reason || "—"}</td>
                                <td className="px-3 py-2 text-[10px] font-mono text-muted-foreground truncate">{f.target_type}/{(f.target_id || "").slice(0, 8)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function Stat({ label, value, className = "" }) {
    return (
        <div className="bg-white p-4">
            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{label}</div>
            <div className={`mt-2 font-display font-black text-2xl sm:text-3xl tracking-tight ${className}`}>{value}</div>
        </div>
    );
}
function Chip({ children }) {
    return <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5 bg-secondary/50">{children}</span>;
}
function ActionChip({ action }) {
    const good = ["accept", "complete"].includes(action);
    const bad = ["false_positive", "dismiss"].includes(action);
    const cls = good ? "border-emerald-500 text-emerald-800 bg-emerald-50"
        : bad ? "border-rose-500 text-rose-800 bg-rose-50"
        : "border-border";
    return <span className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${cls}`}>{action}</span>;
}
function FilterSelect({ label, value, onChange, options, testId }) {
    return (
        <label className="inline-flex items-center gap-2 text-xs">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{label}</span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 px-2 border border-border bg-white text-xs"
                data-testid={testId}
            >
                {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </label>
    );
}
